import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper,
  Card,
  CardContent
} from '@mui/material';
import { 
  Person as PersonIcon
} from '@mui/icons-material';
import { 
  FaUser, 
  FaExclamationTriangle, 
  FaWrench,
  FaRedoAlt,
  FaShieldAlt
} from 'react-icons/fa';
import { 
  MdHealthAndSafety, 
  MdAccessibilityNew, 
  MdWarning 
} from 'react-icons/md';
import { 
  GiBodyBalance, 
  GiMuscleUp, 
  GiToolbox 
} from 'react-icons/gi';
import { 
  HiOutlineExclamation, 
  HiOutlineTrendingUp 
} from 'react-icons/hi';
import { 
  RiAlarmWarningLine, 
  RiBodyScanLine 
} from 'react-icons/ri';
import { SmartCTA } from '../components/SmartCTAs';
import marketingTheme from '../themes/marketingTheme';

const ErgonomicAnalytics = () => {
  const features = [
    {
      icon: <FaRedoAlt style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Repetitive Motions',
      description: 'Analysis of Repetitive Motion Risk Factors and Key Ergonomic Events'
    },
    {
      icon: <RiBodyScanLine style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Unsafe Lifting',
      description: 'Identification of Unsafe Lifting Techniques and Postures'
    },
    {
      icon: <RiAlarmWarningLine style={{ fontSize: 60, color: marketingTheme.palette.primary.main }} />,
      title: 'Proximity to Hazardous Tools',
      description: 'Analysis of Proximity to Hazardous Tools and Equipment'
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#24242a', minHeight: '100vh', pt: 12 }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h1" sx={{ 
            color: 'white', 
            mb: 2,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            fontWeight: 700
          }}>
            Ergonomic Analytics
          </Typography>
          <Typography variant="h5" sx={{ 
            color: marketingTheme.palette.primary.main, 
            mb: 4,
            fontWeight: 400
          }}>
            Vision AI for Manual Workflow Ergonomics.
          </Typography>
        </Box>

        {/* Main Content Grid */}
        <Grid container spacing={6} sx={{ mb: 8 }}>
          <Grid item xs={12} lg={8}>
            <Paper sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              p: 4,
            }}>
              <Typography variant="body1" sx={{ 
                color: 'rgba(255, 255, 255, 0.8)', 
                fontSize: '1.1rem',
                lineHeight: 1.8,
                mb: 4
              }}>
                Ergonomic issues are not immediately apparent. There is a lack of data on ergonomic activities in high-touch production environments. This leads to ineffective process design. Ineffectively designed processes have excess motion which can lead to repetitive motion injuries and reduced productivity.
              </Typography>
              
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <img 
                  src="/images/worker_ergonomics.png" 
                  alt="AI Vision for Ergonomic Analytics" 
                  style={{ 
                    maxWidth: '100%', 
                    height: 'auto',
                    borderRadius: '8px'
                  }} 
                />
              </Box>

              <Typography variant="body1" sx={{ 
                color: 'rgba(255, 255, 255, 0.8)', 
                fontSize: '1.1rem',
                lineHeight: 1.8,
                mb: 4
              }}>
                MotionLogic® makes sense of production events from camera vision. Providing insights on repetitive motions, unsafe lifting, and proximity to hazardous tools.
                <br /><br />
                MotionLogic® can infer skeleton joint angles, to identify ergonomic risk factors and key ergonomic events.
                <br /><br />
                The AI can visually infer repetitive motions, proximity to hazardous tools, proximity to hot work, and proximity to live work.
                <br /><br />
                Proactive reporting can profile the extent and frequency of key risks, and provide real-time audible alerts based upon risk factors.
                <br /><br />
                Safety personnel, through the Event Builder interface, can configure or define which ergonomic events to monitor.
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Paper sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              p: 4,
              textAlign: 'center'
            }}>
              <Typography variant="h5" sx={{ 
                color: marketingTheme.palette.primary.main, 
                mb: 3,
                fontWeight: 600
              }}>
                The Cost of Workplace Injuries
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="h2" sx={{ 
                  color: '#eb4d4b', 
                  fontSize: '30px',
                  fontWeight: 'bold',
                  lineHeight: 1
                }}>
                  $1,080
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: 'white',
                  fontSize: '22px'
                }}>
                  Per Worker
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h2" sx={{ 
                  color: '#eb4d4b', 
                  fontSize: '30px',
                  fontWeight: 'bold',
                  lineHeight: 1
                }}>
                  $1M
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: 'white',
                  fontSize: '22px'
                }}>
                  Per Death
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h2" sx={{ 
                  color: '#eb4d4b', 
                  fontSize: '30px',
                  fontWeight: 'bold',
                  lineHeight: 1
                }}>
                  A$11,803
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: 'white',
                  fontSize: '22px'
                }}>
                  Median Compensation Paid Per Injury
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Features Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ 
            color: 'white', 
            textAlign: 'center', 
            mb: 6,
            fontWeight: 600
          }}>
            Purpose
          </Typography>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  height: '100%',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 8px 25px rgba(237, 185, 46, 0.3)'
                  }
                }}>
                  <CardContent sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <Box sx={{ mb: 3 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" sx={{ 
                      color: 'white', 
                      mb: 2,
                      fontWeight: 600
                    }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      lineHeight: 1.6
                    }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Info Bubble Section */}
        <Grid item xs={12} lg={12}>
          <Paper sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            p: 4,
          }}>
            <Typography variant="body1" sx={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              fontSize: '1.1rem',
              lineHeight: 1.8,
              mb: 4
            }}>
              MotionLogic® infers skeleton joint angles, to identify ergonomic risk factors and key ergonomic events.
              <br /><br />
              The AI can visually infer repetitive motions, proximity to hazardous tools, proximity to hot work, and proximity to live work.
              <br /><br />
              Proactive reporting can profile the extent and frequency of key risks, and provide real-time audible alerts based upon risk factors.
              <br /><br />
              Safety personnel, through the Event Builder interface, can configure or define which ergonomic events to monitor.
            </Typography>
            <SmartCTA
              context="experience-demo"
              fallbackHref="/contact"
              fallbackText="Learn More"
              variant="contained"
              size="large"
              sx={{
                backgroundColor: marketingTheme.palette.primary.main,
                color: '#000',
                fontSize: '1.1rem',
                px: 4,
                py: 1.5,
                '&:hover': {
                  backgroundColor: marketingTheme.palette.primary.dark,
                },
              }}
            />
          </Paper>
        </Grid>

        {/* Privacy Notice */}
        <Paper sx={{ 
          backgroundColor: 'rgba(29, 209, 161, 0.1)', 
          border: '1px solid rgba(29, 209, 161, 0.3)',
          p: 4, 
          display: 'flex',
          alignItems: 'center',
          gap: 3
        }}>
          <FaShieldAlt style={{ fontSize: 60, color: '#1dd1a1', flexShrink: 0 }} />
          <Box>
            <Typography variant="h5" sx={{ 
              color: '#1dd1a1',
              fontWeight: 600,
              mb: 2
            }}>
              Pixel Privacy
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              lineHeight: 1.6
            }}>
              MotionLogic's camera technology preserves the privacy of individuals in visual scenes.
              We pseudonymise our data, and remove individually identifying features, including blurring faces.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ErgonomicAnalytics;
